import { GrantApplication } from "@/api/applicationQueries"
import { ColumnDef } from "@tanstack/react-table"
import { ArrowUpDown, CircleCheckIcon, CircleXIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import ReviewStatusBadge from "../ReviewStatusBadge/ReviewStatusBadge"
import { Badge } from "../ui/badge"
import { Flag32 as Flag, type FlagProps } from "@weston/react-world-flags"

type ColumnWithUserRoles<TData, TValue> = ColumnDef<TData, TValue> & {
    userRoles?: string[]
    accessorKey: string
}

export const CountryWithFlag = ({
    country,
    code,
}: {
    country: string
    code: FlagProps["code"]
}) => {
    return (
        <div className="flex gap-1 items-center">
            <Flag code={code} className="size-6 rounded-sm overflow-clip" />
            <span className="ml-2">{country}</span>
        </div>
    )
}

function countryMapper(country: string) {
    switch (country) {
        case "canada": {
            return CountryWithFlag({
                country: "Canada",
                code: "CA",
            })
        }
        case "scotland": {
            return CountryWithFlag({
                country: "Scotland",
                code: "GB-SCT",
            })
        }
    }
}

function convertTimestampToBritishDate(timestamp: {
    seconds: number
    nanoseconds: number
}): string {
    const date = new Date(
        timestamp.seconds * 1000 + timestamp.nanoseconds / 1000000
    )
    const options: Intl.DateTimeFormatOptions = {
        day: "numeric",
        month: "long",
        year: "numeric",
    }
    return date.toLocaleDateString("en-GB", options)
}

export const columns: ColumnWithUserRoles<GrantApplication, unknown>[] = [
    {
        accessorKey: "applicationId",
        enableHiding: true,
    },
    // Status Applicant
    {
        accessorKey: "status",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() =>
                        column.toggleSorting(column.getIsSorted() === "asc")
                    }
                >
                    Application Status
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ cell }) => {
            const value = cell.getValue() as string

            return value ? (
                <Badge
                    // @ts-expect-error - This is a known issue with the badge component
                    variant={value.toLowerCase()}
                >
                    {value}
                </Badge>
            ) : (
                "-"
            )
        },
        userRoles: ["applicant"],
    },
    // Date Created Applicant
    {
        accessorKey: "dateCreated",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() =>
                        column.toggleSorting(column.getIsSorted() === "asc")
                    }
                >
                    Date Created
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ cell }) => {
            const value = cell.getValue() as {
                seconds: number
                nanoseconds: number
            }
            return value ? convertTimestampToBritishDate(value) : "-"
        },
        userRoles: ["applicant"],
    },
    // Date Submitted Applicant, Admin, Trustee
    {
        accessorKey: "dateSubmitted",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() =>
                        column.toggleSorting(column.getIsSorted() === "asc")
                    }
                >
                    Date Submitted
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ cell }) => {
            const value = cell.getValue() as {
                seconds: number
                nanoseconds: number
            }
            return value ? convertTimestampToBritishDate(value) : "-"
        },
        userRoles: ["admin", "trustee", "applicant"],
    },
    // Review Status - Admin, Trustee
    {
        accessorKey: "reviewStatus",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() =>
                        column.toggleSorting(column.getIsSorted() === "asc")
                    }
                >
                    Review Status
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ cell }) => {
            const value = cell.getValue()
            if (
                typeof value === "string" &&
                ["new", "review", "accepted", "declined"].includes(value)
            ) {
                const status = value as
                    | "new"
                    | "review"
                    | "accepted"
                    | "declined"
                return <ReviewStatusBadge content={status} status={status} />
            }
            return null
        },
        userRoles: ["admin"],
    },
    // Category - Admin, Trustee
    {
        accessorKey: "category",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() =>
                        column.toggleSorting(column.getIsSorted() === "asc")
                    }
                >
                    Category/Tier
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ cell }) => {
            const value = cell.getValue() as string

            return value ? (
                <Badge
                    // @ts-expect-error - This is a known issue with the badge component
                    variant={value
                        .toLowerCase()
                        .replace("category ", "category-")}
                >
                    {value}
                </Badge>
            ) : (
                "-"
            )
        },
        userRoles: ["admin", "trustee"],
    },
    // Charity Number - Applicant, Admin, Trustee
    {
        accessorKey: "charityNumber",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() =>
                        column.toggleSorting(column.getIsSorted() === "asc")
                    }
                >
                    Charity Number
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        userRoles: ["admin", "applicant"],
    },
    // Organisation Name - Applicant, Admin, Trustee
    {
        accessorKey: "orgName",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() =>
                        column.toggleSorting(column.getIsSorted() === "asc")
                    }
                >
                    Organisation Name
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        userRoles: ["admin", "trustee", "applicant"],
    },
    // Requested Amount - Applicant, Admin, Trustee
    {
        accessorKey: "requestedAmount",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() =>
                        column.toggleSorting(column.getIsSorted() === "asc")
                    }
                >
                    Requested Amount
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ cell }) => {
            const value = parseFloat(cell.getValue() as string)
            return value
                ? `${Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value)}`
                : "-"
        },
        userRoles: ["admin", "trustee", "applicant"],
    },
    // Grant Amount - Admin, Trustee
    {
        accessorKey: "grantAmount",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() =>
                        column.toggleSorting(column.getIsSorted() === "asc")
                    }
                >
                    Grant Amount
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ cell }) => {
            const value = parseFloat(cell.getValue() as string)
            return value
                ? `${Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value)}`
                : "-"
        },
        userRoles: ["admin"],
    },
    // Country - Admin, Trustee
    {
        accessorKey: "country",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() =>
                        column.toggleSorting(column.getIsSorted() === "asc")
                    }
                >
                    Country
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ cell }) => {
            const value = cell.getValue() as string
            return value ? countryMapper(value) : "-"
        },
        userRoles: ["admin", "trustee"],
    },
    // Territory - Applicant, Admin, Trustee
    {
        accessorKey: "territory",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() =>
                        column.toggleSorting(column.getIsSorted() === "asc")
                    }
                >
                    Territory
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        userRoles: ["admin", "trustee", "applicant"],
    },
    // Voting Status - Admin, Trustee
    {
        accessorKey: "votingStatus",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() =>
                        column.toggleSorting(column.getIsSorted() === "asc")
                    }
                >
                    Voting Status
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ cell }) => {
            const value = (cell.getValue() || "incomplete") as string
            return <ReviewStatusBadge content={value} status={value} />
        },
        userRoles: ["admin", "trustee"],
    },
    // Age Group - Applicant, Admin, Trustee
    {
        accessorKey: "ageGroup",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() =>
                        column.toggleSorting(column.getIsSorted() === "asc")
                    }
                >
                    Age Group
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ cell }) => {
            const value = cell.getValue() as string
            return value ? (
                <Badge variant="age-group">
                    {value.toLowerCase().replace("both", "")}
                </Badge>
            ) : (
                "-"
            )
        },
        userRoles: ["admin", "trustee", "applicant"],
    },
    // Veterans Check - Applicant, Admin, Trustee
    {
        accessorKey: "veteransCheck",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() =>
                        column.toggleSorting(column.getIsSorted() === "asc")
                    }
                >
                    Veterans Check
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ cell }) => {
            const value = cell.getValue()
            return value ? (
                <div className="flex w-full justify-center items-center">
                    <CircleCheckIcon
                        size={24}
                        className="text-status-accepted-text text-center"
                    />
                </div>
            ) : (
                <div className="flex w-full justify-center items-center">
                    <CircleXIcon
                        size={24}
                        className="text-center text-status-declined-text"
                    />
                </div>
            )
        },
        userRoles: ["admin", "trustee", "applicant"],
    },
    // Sector - Applicant, Admin, Trustee
    {
        accessorKey: "sector",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() =>
                        column.toggleSorting(column.getIsSorted() === "asc")
                    }
                >
                    Sector
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ cell }) => {
            const value = cell.getValue() as string
            return value ? (
                <Badge variant="category-b">
                    {value.toLowerCase().replace("arts & music / play", "Arts")}
                </Badge>
            ) : (
                "-"
            )
        },
        userRoles: ["admin", "trustee", "applicant"],
    },
    // Subsector - Applicant, Admin, Trustee
    {
        accessorKey: "subsector",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() =>
                        column.toggleSorting(column.getIsSorted() === "asc")
                    }
                >
                    Subsector
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ cell }) => {
            const value = cell.getValue() as string

            return value ? (
                <Badge variant="category-d">
                    {value.toLowerCase().replace("/new skills", "")}
                </Badge>
            ) : (
                "-"
            )
        },
        userRoles: ["admin", "trustee", "applicant"],
    },
    // Date Decided - Admin, Trustee
    {
        accessorKey: "dateDecide",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() =>
                        column.toggleSorting(column.getIsSorted() === "asc")
                    }
                >
                    Date Decided
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ cell }) => {
            const value = cell.getValue() as {
                seconds: number
                nanoseconds: number
            }
            return value ? convertTimestampToBritishDate(value) : "-"
        },
        userRoles: ["admin", "trustee"],
    },
    // Applicant Notified - Admin
    {
        accessorKey: "applicantNotified",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() =>
                        column.toggleSorting(column.getIsSorted() === "asc")
                    }
                >
                    Applicant Notified
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ cell }) => {
            const value = cell.getValue()
            return value ? (
                <div className="flex w-full justify-center items-center">
                    <CircleCheckIcon
                        size={24}
                        className="text-status-accepted-text text-center"
                    />
                </div>
            ) : (
                <div className="flex w-full justify-center items-center">
                    <CircleXIcon
                        size={24}
                        className="text-center text-status-declined-text"
                    />
                </div>
            )
        },
        userRoles: ["admin"],
    },
    {
        accessorKey: "votingDeadline",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() =>
                        column.toggleSorting(column.getIsSorted() === "asc")
                    }
                >
                    Voting Deadline
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        userRoles: ["admin", "trustee"],
        cell: ({ row }) => {
            const deadline = row.getValue("votingDeadline") as string | null
            if (!deadline) return null

            const date = new Date(deadline)
            const today = new Date()
            const isPastDeadline = date < today

            return (
                <div
                    className={`text-sm ${isPastDeadline ? "text-red-500" : ""}`}
                >
                    {date.toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                    })}
                </div>
            )
        },
    },
]
